import { addDays, format, isAfter, isBefore, isToday } from 'date-fns';

import { parseValidEnumeration, parseValidRange } from '@/components/search/v1/utils/searchValidations';
import { ConflictenFilter } from '@/components/sidebar/conflicten/conflictenFilter/ConflictenFilterDialog';
import {
  nummerComparator,
  SorteringRichting,
  SorteringType,
  stringComparator,
  tijdComparator,
} from '@/components/sidebar/sortField/useSorting';
import { Conflict, ConflictElement, ConflictType } from '@/types/Conflicten';
import { dayOfWeekWithTime, timeWithDayOfWeek } from '@/utils/dateUtils';
import { combineFilters } from '@/utils/functions';
import { parseInzetPositie } from '@/utils/helpers';

const M_DASH = '\u2014';

/** Returns the verkeersdag date key (yyyy-MM-dd) for grouping.
 *  Times between 00:00 and 02:00 belong to the previous verkeersdag. */
export const getVerkeersdagKey = (uitvoeringstijd: string | undefined | null): string => {
  if (!uitvoeringstijd) return '';
  const date = new Date(uitvoeringstijd);
  const verkeersdagDate = date.getHours() < 2 ? addDays(date, -1) : date;
  return format(verkeersdagDate, 'yyyy-MM-dd');
};

export const computeLastAcceptedPerDay = (conflict: Conflict): ConflictElement[] | null => {
  const filteredElementen = conflict.elementen.filter((el) => !el.isGeaccepteerd);
  if (filteredElementen.length !== 0) return null;
  return Object.values(
    conflict.elementen
      .filter((el) => el.isGeaccepteerd)
      .reduce<Record<string, ConflictElement>>((acc, el) => {
        const day = getVerkeersdagKey(el.uitvoeringstijd);
        acc[day] = el;
        return acc;
      }, {}),
  );
};

export const geefNietGeaccepteerdeElementen = (conflict: Conflict): ConflictElement[] => {
  const nietGeaccepteerd = conflict.elementen.filter((el) => !el.isGeaccepteerd);
  return nietGeaccepteerd.length > 0 ? nietGeaccepteerd : conflict.elementen;
};

export const geefInzetOmschrijvingVoorElement = (element?: ConflictElement) => {
  if (element?.claimSoort) {
    return element.claimSoort;
  }
  if (element?.treinNummer) {
    const versterkteTreinCharacter = `${element.oorspronkelijkeDienst === null ? '+' : ''}`;
    return (
      element.treinNummer +
      (parseInzetPositie(element.inzetPositie)
        ? `-${parseInzetPositie(element.inzetPositie)}${versterkteTreinCharacter}`
        : `${versterkteTreinCharacter}`)
    );
  }
  return null;
};

export const geefVerkeersdagTijdstipVoorElement = (element: ConflictElement | undefined | null) => {
  if (!element?.uitvoeringstijd) return M_DASH;
  const date = new Date(element.uitvoeringstijd);
  // Times between 00:00 and 02:00 still belong to the previous verkeersdag
  const verkeersdagDate = date.getHours() < 2 ? addDays(date, -1) : date;
  return timeWithDayOfWeek(verkeersdagDate);
};

export const isVerkeersdagVandaag = (element: ConflictElement | undefined | null) => {
  if (!element?.uitvoeringstijd) return false;
  const date = new Date(element.uitvoeringstijd);
  const verkeersdagDate = date.getHours() < 2 ? addDays(date, -1) : date;
  return isToday(verkeersdagDate);
};

// Alleen verwerken op types die wij al hadden gespecificeerd in BAM
export const KNOWN_CONFLICT_TYPES = [
  'GEACCEPTEERD',
  'PLAATS',
  'MANCO',
  'VERKEERD_MATERIEEL',
  'VERSTERKT_MATERIEEL',
  'TIJD',
  'PERRONLENGTE',
  'KOPPEL',
] as const satisfies readonly ConflictType[];

const geefTijdstipVoorConflictElement = (ce: ConflictElement) =>
  ce?.uitvoeringstijd ? new Date(ce?.uitvoeringstijd) : undefined;

export const geefTijdstipVoorConflict = (conflict: Conflict) => {
  const [eersteElement] = geefNietGeaccepteerdeElementen(conflict);
  return eersteElement?.uitvoeringstijd ? new Date(eersteElement.uitvoeringstijd) : undefined;
};

export const geefTreinnummerVoorConflict = (conflict: Conflict) => {
  const [eersteElement, tweedeElement] = geefNietGeaccepteerdeElementen(conflict);
  return eersteElement?.treinNummer ?? tweedeElement?.treinNummer;
};

export const geefOorspronkelijkeDienstVoorConflict = (conflict: Conflict) => {
  const [eersteElement, tweedeElement] = geefNietGeaccepteerdeElementen(conflict);
  return eersteElement?.oorspronkelijkeDienst ?? tweedeElement?.oorspronkelijkeDienst ?? conflict.oorspronkelijkeDienst;
};

export const geefStellenOfOorspronkelijkDienstVoorConflict = (conflict: Conflict) => {
  let eenheid = conflict.eenheidNummer;

  if (conflict.betrokkenWerklijnen) {
    eenheid = conflict.betrokkenWerklijnen
      .map((b) => b.eenheidNummer)
      .filter((n) => !!n)
      .join(', ');
  }

  return eenheid === '' ? geefOorspronkelijkeDienstVoorConflict(conflict) : eenheid;
};

export const geefDienstregelpuntVoorConflict = (conflict: Conflict) => {
  if (conflict.allElementsAccepted) return '';
  const [eersteElement] = geefNietGeaccepteerdeElementen(conflict);
  return eersteElement?.ketenAanpassen === true
    ? `${eersteElement?.dienstregelpunt}•`
    : (eersteElement?.dienstregelpunt ?? M_DASH);
};

export const geefPerronSpoorVoorConflict = (conflict: Conflict, variant: 'default' | 'tooltip' = 'default') => {
  const [eersteElement] = geefNietGeaccepteerdeElementen(conflict);
  if (!eersteElement?.perronSpoor) return null;
  const { perronSpoor } = eersteElement;
  if (variant === 'tooltip') return `(Sp.${perronSpoor})`;
  return `Sp. ${perronSpoor}`;
};

export const geefVerstoringspuntenVoorConflict = (conflict?: Conflict | ConflictElement): string => {
  const kosten = conflict?.kosten;
  if (kosten == null || kosten === 0) {
    return '';
  }
  const roundedKosten = Math.round(kosten);
  return `${roundedKosten.toLocaleString('nl-NL')} pt`;
};

export const geefInzetVoorConflict = (conflict: Conflict) => {
  if (conflict.allElementsAccepted) return '';
  const [eersteElement, tweedeElement] = geefNietGeaccepteerdeElementen(conflict);
  return geefInzetOmschrijvingVoorElement(eersteElement) ?? geefInzetOmschrijvingVoorElement(tweedeElement) ?? M_DASH;
};

const conflictenComparator = (
  conflictA: Conflict,
  conflictB: Conflict,
  sortType: SorteringType | undefined,
  sortDirection: SorteringRichting | undefined,
) => {
  const uitvoeringstijdA = geefTijdstipVoorConflict(conflictA);
  const uitvoeringstijdB = geefTijdstipVoorConflict(conflictB);
  const fallbackComparator = tijdComparator(uitvoeringstijdA, uitvoeringstijdB, SorteringRichting.UP);

  if (sortType === SorteringType.DATE_TIME) {
    return tijdComparator(uitvoeringstijdA, uitvoeringstijdB, sortDirection);
  }
  if (sortType === SorteringType.CONFLICT_SOORT) {
    return stringComparator(conflictA.type, conflictB.type, sortDirection, () => fallbackComparator);
  }
  if (sortType === SorteringType.EENHEID_NUMMER) {
    return nummerComparator(
      Number(conflictA.eenheidNummer),
      Number(conflictB.eenheidNummer),
      sortDirection,
      () => fallbackComparator,
    );
  }
  if (sortType === SorteringType.DIENST) {
    return stringComparator(
      geefOorspronkelijkeDienstVoorConflict(conflictA),
      geefOorspronkelijkeDienstVoorConflict(conflictB),
      sortDirection,
      () => fallbackComparator,
    );
  }
  if (sortType === SorteringType.TREIN_NUMMER) {
    const treinnummerA = geefTreinnummerVoorConflict(conflictA);
    const treinnummerB = geefTreinnummerVoorConflict(conflictB);
    return nummerComparator(treinnummerA, treinnummerB, sortDirection, () => fallbackComparator);
  }
  if (sortType === SorteringType.VERSTORINGSPUNTEN) {
    return nummerComparator(
      Number(conflictA.kosten),
      Number(conflictB.kosten),
      sortDirection,
      () => fallbackComparator,
    );
  }

  // Default sorteer op uitvoeringstijd up
  return fallbackComparator;
};

const conflictElementenComparatorDrglPunten = (
  a: ConflictElement,
  b: ConflictElement,
  dienstregelpunten: string[] | undefined,
) => {
  // Toon eerste conflictelement vanaf nu of de eerstvolgende vanaf nu uit die werklijn met een vertrek op het door de gebruiker gekozen dienstregelpunt.
  const aMatchesDienstregelpunten = dienstregelpunten?.includes(a.dienstregelpunt);
  const bMatchesDienstregelpunten = dienstregelpunten?.includes(b.dienstregelpunt);

  if (aMatchesDienstregelpunten && !bMatchesDienstregelpunten) {
    return -1;
  }
  if (!aMatchesDienstregelpunten && bMatchesDienstregelpunten) {
    return 1;
  }
  return 0;
};

const conflictElementenComparatorTijden = (a: ConflictElement, b: ConflictElement) => {
  // Toon eerste conflictelement vanaf nu of de eerstvolgende vanaf nu uit die werklijn met een vertrek op het door de gebruiker gekozen dienstregelpunt.
  const aUitvoeringsTijd = geefTijdstipVoorConflictElement(a);
  const bUitvoeringsTijd = geefTijdstipVoorConflictElement(b);

  if (!aUitvoeringsTijd) {
    return -1;
  }
  if (!bUitvoeringsTijd) {
    return 1;
  }
  return isBefore(aUitvoeringsTijd, bUitvoeringsTijd) ? -1 : 1;
};

const eenheidOfTreinnummerRangeFilter = (fromValue: number, toValue: number, conflict: Conflict) => {
  const eenheidNummer = Number(conflict.eenheidNummer);
  if (eenheidNummer >= fromValue && eenheidNummer <= toValue) {
    return true;
  }
  return conflict.elementen.map((e) => e.treinNummer).some((t) => t >= fromValue && t <= toValue);
};

const filterOpKnownTypes = () => (conflict: Conflict) => KNOWN_CONFLICT_TYPES.includes(conflict.type);

const filterConflictOpEenheidEnTreinnummer = (eenheidOfTreinnummer?: string) => (conflict: Conflict) => {
  if (!eenheidOfTreinnummer) {
    return true;
  }
  const validEnumeration = parseValidEnumeration(eenheidOfTreinnummer);
  const validEenheidOfTreinRange = parseValidRange(eenheidOfTreinnummer);
  if (validEnumeration) {
    return (
      validEnumeration.includes(Number(conflict.eenheidNummer)) ||
      conflict.elementen.map((e) => e.treinNummer).some((t) => validEnumeration.includes(t))
    );
  }
  if (validEenheidOfTreinRange) {
    const [from, to] = validEenheidOfTreinRange;
    return eenheidOfTreinnummerRangeFilter(from, to, conflict);
  }
  return (
    eenheidOfTreinnummer === conflict.eenheidNummer ||
    conflict.elementen.map((e) => e.treinNummer).includes(Number(eenheidOfTreinnummer)) ||
    conflict?.betrokkenWerklijnen?.map((e) => e.eenheidNummer).includes(eenheidOfTreinnummer) ||
    false
  );
};

const filterConflictOpDienstregelpunten = (dienstregelpunten?: string[]) => (conflict: Conflict) => {
  if (!dienstregelpunten || dienstregelpunten.length === 0) {
    return true;
  }
  return conflict.elementen.map((e) => e.dienstregelpunt).some((cd) => dienstregelpunten.includes(cd));
};

const matTypeConflict = (conflict: Conflict) => `${conflict.eenheidSoort}-${conflict.eenheidAanduiding}`;
const matTypeElement = (element: ConflictElement) => `${element.materieelSoort}-${element.materieelAanduiding}`;

const filterConflictOpMaterieelType = (materieelTypen?: string[]) => (conflict: Conflict) => {
  if (!materieelTypen || materieelTypen.length === 0) {
    return true;
  }
  if (materieelTypen.some((mt) => mt.toUpperCase() === matTypeConflict(conflict).toUpperCase())) {
    return true;
  }
  return conflict.elementen
    .map((e) => matTypeElement(e))
    .some((em) => materieelTypen.some((mt) => mt.toUpperCase() === em.toUpperCase()));
};

const filterConflictOpConflictType = (conflictTypen?: string[]) => (conflict: Conflict) => {
  if (!conflictTypen || conflictTypen.length === 0) {
    return true;
  }
  if (conflictTypen.includes('GEACCEPTEERD') && (conflict.partOfElementsAccepted || conflict.allElementsAccepted)) {
    return true;
  }
  return conflictTypen.some((ct) => ct === conflict.type);
};

const filterConflictOpDatumTijd = (datumVan?: string, datumTotEnMet?: string) => (conflict: Conflict) => {
  if (!datumVan || !datumTotEnMet) {
    return true;
  }
  const van = new Date(datumVan);
  const tot = new Date(datumTotEnMet);
  const elementenVolgensTijd = [...conflict.elementen].sort(conflictElementenComparatorTijden);

  const eersteElementInKeten = geefTijdstipVoorConflictElement(elementenVolgensTijd[0]);
  const laatsteElementInKeten =
    conflict.elementen.length > 1
      ? geefTijdstipVoorConflictElement(elementenVolgensTijd[conflict.elementen.length - 1])
      : undefined;
  if (!eersteElementInKeten) {
    return true;
  }

  // Wanneer er geen keten is (dus 1 element) of het conflict een perronlengteconflict is,
  // kijken we alleen naar het tijdstip van het eerste element in de keten
  if (conflict.type === 'PERRONLENGTE' || !laatsteElementInKeten) {
    return conflict.elementen.some(() => {
      const tijd = geefTijdstipVoorConflictElement(elementenVolgensTijd[0]);
      return tijd && tijd >= van && tijd <= tot;
    });
  }

  return (
    isBefore(eersteElementInKeten, new Date(datumTotEnMet)) &&
    (!laatsteElementInKeten || isAfter(laatsteElementInKeten, new Date(datumVan)))
  );
};

const filterConflictOpDienstregelpuntenEnDatumTijd = (
  dienstregelpunten?: string[],
  datumVan?: string,
  datumTotEnMet?: string,
) => {
  const dienstregelpuntenFilter = filterConflictOpDienstregelpunten(dienstregelpunten);
  const datumTijdFilter = filterConflictOpDatumTijd(datumVan, datumTotEnMet);

  if (!dienstregelpunten?.length || !datumVan || !datumTotEnMet) {
    return (conflict: Conflict) => dienstregelpuntenFilter(conflict) && datumTijdFilter(conflict);
  }

  const van = new Date(datumVan);
  const totEnMet = new Date(datumTotEnMet);

  return (conflict: Conflict) =>
    conflict.elementen.some((element) => {
      const uitvoeringstijd = geefTijdstipVoorConflictElement(element);
      return (
        dienstregelpunten.includes(element.dienstregelpunt) &&
        uitvoeringstijd !== undefined &&
        uitvoeringstijd >= van &&
        uitvoeringstijd <= totEnMet
      );
    });
};

export const filterEnSorteerConflicten = (conflicten: Conflict[] | undefined, filter: ConflictenFilter) => {
  if (!conflicten) {
    return [];
  }
  const toonGeaccepteerdeElementen = filter.conflictTypen?.includes('GEACCEPTEERD');
  const conflictenVoorFilter = conflicten.map((conflict) => ({
    ...conflict,
    elementen: [
      ...(toonGeaccepteerdeElementen && conflict.geaccepteerdeElementen?.length
        ? conflict.geaccepteerdeElementen
        : conflict.elementen),
    ],
  }));
  const filterOpType = filterOpKnownTypes();
  const eenheidEnTreinnummerFilter = filterConflictOpEenheidEnTreinnummer(filter.eenheidOfTreinnummer);
  const dienstregelpuntenEnDatumTijdFilter = filterConflictOpDienstregelpuntenEnDatumTijd(
    filter.dienstregelpunten,
    filter.datumVan,
    filter.datumTotEnMet,
  );
  const materieelTypenFilter = filterConflictOpMaterieelType(filter.materieelTypen);
  const conflictTypenFilter = filterConflictOpConflictType(filter.conflictTypen);
  const gefilterdConflicten: Conflict[] = conflictenVoorFilter
    .filter(
      combineFilters(
        filterOpType,
        eenheidEnTreinnummerFilter,
        dienstregelpuntenEnDatumTijdFilter,
        materieelTypenFilter,
        conflictTypenFilter,
      ),
    )
    .sort((conflictA, conflictB) =>
      conflictenComparator(conflictA, conflictB, filter.sorteringType, filter.sorteringRichting),
    );

  // Bepaal of de gesorteerde order afwijken van de originele order om het later visual te laten zien
  gefilterdConflicten.forEach((conflict) => {
    const originalOrder = [...conflict.elementen];
    conflict.elementen.sort((a, b) => conflictElementenComparatorDrglPunten(a, b, filter.dienstregelpunten));
    conflict.elementen = conflict.elementen.map((element, index) => {
      if (
        element !== originalOrder[index] &&
        ['MANCO', 'VERKEERD_MATERIEEL', 'VERSTERKT_MATERIEEL'].includes(conflict.type)
      ) {
        return {
          ...element,
          ketenAanpassen: true,
        };
      }
      return element;
    });
  });

  return gefilterdConflicten.sort((conflictA, conflictB) =>
    conflictenComparator(conflictA, conflictB, filter.sorteringType, filter.sorteringRichting),
  );
};

export const conflictTypeIcon: (type: ConflictType) => string = (type) => {
  switch (type) {
    case 'MANCO':
      return 'TrainManco'; // trein met kruisje
    case 'PLAATS':
      return 'Plaatsconflict'; // locatie-pin
    case 'VERKEERD_MATERIEEL':
      return 'VerkeerdMat'; // twee vierkantjes met pijltje
    case 'VERSTERKT_MATERIEEL':
      return 'Versterken'; // plusje met cirkel
    case 'PERRONLENGTE':
      return 'Perronlengte';
    case 'KOPPEL':
      return 'Koppel';
  }
  return 'Clock';
};

export const conflictSoortOmschrijving: (type: ConflictType) => string = (type) => {
  switch (type) {
    case 'MANCO':
      return 'Manco';
    case 'PLAATS':
      return 'Plaats';
    case 'VERKEERD_MATERIEEL':
      return 'Verk. mat.';
    case 'VERSTERKT_MATERIEEL':
      return 'Versterkt';
    case 'PERRONLENGTE':
      return 'Perronlengte';
    case 'KOPPEL':
      return 'Koppel';
  }
  return 'Tijd';
};

export const conflictSoortTooltip: (type: ConflictType) => string = (type) => {
  switch (type) {
    case 'MANCO':
      return 'Manco';
    case 'PLAATS':
      return 'Plaatsconflict';
    case 'VERKEERD_MATERIEEL':
      return 'Verkeerd materieel';
    case 'VERSTERKT_MATERIEEL':
      return 'Versterkt materieel';
    case 'PERRONLENGTE':
      return 'Perronlengte';
    case 'KOPPEL':
      return 'Koppel';
  }
  return 'Tijdsconflict';
};

export const titleInfo = (conflict: Conflict) => {
  if (conflict.partOfElementsAccepted) return 'Dit conflict bevat geaccepteerde inzetten';
  if (conflict.allElementsAccepted) return 'Alle inzetten binnen dit conflict zijn geaccepteerd';

  const elementenVolgensTijd = [...conflict.elementen].sort(conflictElementenComparatorTijden);
  const eersteElement = elementenVolgensTijd[0];
  const laatsteElement = elementenVolgensTijd[conflict.elementen.length - 1];

  const conflictVan = `Conflict van: ${dayOfWeekWithTime(geefTijdstipVoorConflictElement(eersteElement))} - ${eersteElement?.dienstregelpunt}`;
  const conflictTot =
    conflict.elementen.length > 1
      ? `\nConflict tot: ${dayOfWeekWithTime(geefTijdstipVoorConflictElement(laatsteElement))} - ${elementenVolgensTijd[1]?.dienstregelpunt}`
      : '';

  return conflictVan + conflictTot;
};
